import { useEffect, useRef } from "react";
import {
  attachLivePlayback,
  createVideoBufferingHandlers,
} from "../../lib/live-playback.ts";

export type StreamVideoProps = {
  playlistUrl: string | null;
  streamType?: "hls" | "webrtc";
  className?: string;
  videoClassName?: string;
  enabled?: boolean;
  objectFit?: "cover" | "contain";
  onStreamError: () => void;
  onBufferingChange?: (busy: boolean) => void;
  onStreamLoadingChange?: (loading: boolean) => void;
};

export function StreamVideo({
  playlistUrl,
  streamType = "webrtc",
  className,
  videoClassName,
  enabled = true,
  objectFit = "cover",
  onStreamError,
  onBufferingChange,
  onStreamLoadingChange,
}: StreamVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onErrRef = useRef(onStreamError);
  const onBufRef = useRef(onBufferingChange);
  const onLoadRef = useRef(onStreamLoadingChange);
  onErrRef.current = onStreamError;
  onBufRef.current = onBufferingChange;
  onLoadRef.current = onStreamLoadingChange;

  useEffect(() => {
    if (!enabled || !playlistUrl) return;
    const video = videoRef.current;
    if (!video) return;

    const buffering = createVideoBufferingHandlers((busy) =>
      onBufRef.current?.(busy),
    );
    buffering.attach(video);

    let loadSettled = false;
    const reportLoadEnd = () => {
      if (loadSettled) return;
      loadSettled = true;
      onLoadRef.current?.(false);
    };

    onLoadRef.current?.(true);
    video.addEventListener("playing", reportLoadEnd, { once: true });
    video.addEventListener("canplay", reportLoadEnd, { once: true });

    const abort = new AbortController();
    const session = attachLivePlayback({
      playlistUrl,
      streamType,
      video,
      signal: abort.signal,
      onFatalError: () => {
        reportLoadEnd();
        onErrRef.current?.();
      },
      onRecovering: () => {
        onLoadRef.current?.(true);
      },
    });

    if (!session) {
      reportLoadEnd();
      return undefined;
    }

    return () => {
      abort.abort();
      session.destroy();
      buffering.detach(video);
      buffering.clear();
      reportLoadEnd();
      video.removeEventListener("playing", reportLoadEnd);
      video.removeEventListener("canplay", reportLoadEnd);
    };
  }, [playlistUrl, enabled, streamType]);

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#0b1220",
        overflow: "hidden",
      }}
    >
      <video
        ref={videoRef}
        className={videoClassName}
        playsInline
        muted
        autoPlay
        controls={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit,
          display: "block",
        }}
      />
    </div>
  );
}
